export type AttackerState = 'queued' | 'on_conveyor' | 'waiting' | 'exhausted';

export interface Orb {
  colorIndex: number;
  ammo: number;
  maxAmmo: number;
  mystery?: boolean;
}

export interface AttackerData {
  id: number;
  orbs: Orb[];
  state: AttackerState;
  /** Which queue lanes this attacker spans (e.g. [0,1] for a 2-orb). Set during queue placement. */
  queueLanes?: number[];
  /** Defrost counter — number of attacker exhaustions needed to thaw. 0 or undefined = not frozen. */
  frozen?: number;
  /** If true, this is a hammer — a special queue item that breaks barricades instead of shooting cubes. */
  isHammer?: boolean;
}

// ── Attacker Factory Functions ───────────────────────────────────────────────
// Three factory functions cover all attacker types:
//   createAttacker()            — single-color attacker (1+ orbs, all same color)
//   createMultiColorAttacker()  — multi-color attacker (each orb has its own color)
//   createHammer()              — special queue item that breaks barricades
// All share a module-level auto-incrementing ID counter (reset via resetAttackerIds).

let nextId = 1;

/**
 * Create an attacker with N orbs (one per slot).
 * For single-color attackers: all orbs share the same color, ammo is split across orbs.
 * For multi-color: use createMultiColorAttacker instead.
 */
export function createAttacker(colorIndex: number, ammo: number, size: number = 1, mystery: boolean = false, frozen?: number): AttackerData {
  // Reduce orb count if ammo is too low for every orb to get at least 1 (never inflate ammo)
  const safeSize = Math.min(size, Math.max(ammo, 1));
  const orbs: Orb[] = [];
  const baseAmmo = Math.floor(ammo / safeSize);
  let remainder = ammo % safeSize;

  for (let i = 0; i < safeSize; i++) {
    const orbAmmo = baseAmmo + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder--;
    orbs.push({ colorIndex, ammo: orbAmmo, maxAmmo: orbAmmo, mystery });
  }

  const attacker: AttackerData = {
    id: nextId++,
    orbs,
    state: 'queued',
  };
  if (frozen && frozen > 0) attacker.frozen = frozen;
  return attacker;
}

/**
 * Create a multi-color attacker from explicit per-orb specs.
 * Each orb can have a different color and ammo.
 */
export function createMultiColorAttacker(orbSpecs: { colorIndex: number; ammo: number; mystery?: boolean }[]): AttackerData {
  return {
    id: nextId++,
    orbs: orbSpecs.map(spec => ({ colorIndex: spec.colorIndex, ammo: spec.ammo, maxAmmo: spec.ammo, mystery: spec.mystery ?? false })),
    state: 'queued',
  };
}

/** Create a hammer item — a special queue entry that breaks barricades. No color, no ammo. */
export function createHammer(): AttackerData {
  return {
    id: nextId++,
    orbs: [{ colorIndex: -1, ammo: 0, maxAmmo: 0 }],
    state: 'queued',
    isHammer: true,
  };
}

export function resetAttackerIds(): void {
  nextId = 1;
}

// ── Convenience helpers ─────────────────────────────────────────────────────

/** Number of slots this attacker occupies (orbs.length). */
export function attackerSize(a: AttackerData): number {
  return a.orbs.length;
}

/** Primary color (first orb). Used for single-orb attackers and display. */
export function primaryColor(a: AttackerData): number {
  return a.orbs[0].colorIndex;
}

/** Sum of ammo across all orbs. */
export function totalAmmo(a: AttackerData): number {
  return a.orbs.reduce((sum, o) => sum + o.ammo, 0);
}

/** True if every orb has ammo <= 0. */
export function isExhausted(a: AttackerData): boolean {
  return a.orbs.every(o => o.ammo <= 0);
}

/** True if this attacker is frozen (has a defrost counter > 0). */
export function isFrozen(a: AttackerData): boolean {
  return (a.frozen ?? 0) > 0;
}
